/**
 * Campos de formulário do Lumma Store.
 *
 * Cada campo carrega label associada, mensagem de erro anunciada e
 * `aria-invalid`. O handoff pede foco visível acessível na implementação real
 * — quem opera um balcão navega por teclado o dia inteiro.
 */
import { useId, useState } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

import { Icone } from './Icone'

/**
 * Contexto do campo, que decide altura e raio.
 *
 * São dois valores do handoff, e não uma escolha: o sistema usa 38px com raio
 * 8 (spec 01, "Controles"); as telas de acesso usam 52px com raio 10, que é o
 * `--radius-auth`. Um componente com um raio fixo estaria errado em um dos
 * dois lugares — e estava: fixava 10px, então os campos das telas internas
 * saíam com o raio do login.
 *
 * O padrão é `sistema` porque são 17 telas internas contra 3 de acesso: o
 * default certo é o que a maioria usa, senão alguém esquece na décima.
 */
export type VarianteCampo = 'sistema' | 'auth'

const MEDIDAS: Record<VarianteCampo, { altura: number; raio: string }> = {
  sistema: { altura: 38, raio: 'rounded-sistema' },
  auth: { altura: 52, raio: 'rounded-auth' },
}

/**
 * A largura do campo, pelo CONTEÚDO que ele guarda — nunca pelo espaço que
 * sobra na tela.
 *
 * Um campo de CEP com 700 px não é generoso, é desproporcional: a pessoa
 * digita oito dígitos numa caixa do tamanho de um parágrafo, e o olho perde a
 * relação entre o rótulo à esquerda e o fim do controle. A largura é uma dica
 * de quanto se espera ali — e uma dica errada custa uma leitura a mais em cada
 * campo do formulário.
 *
 * `total` continua o padrão porque a maioria dos campos vive dentro de um grid
 * que já decide a coluna. Estas medidas são para quando o campo está SOLTO,
 * numa coluna larga.
 */
export type LarguraCampo = 'curta' | 'media' | 'longa' | 'total'

const LARGURA: Record<LarguraCampo, string> = {
  /** CEP, número, UF, quantidade, data. */
  curta: 'max-w-[180px]',
  /** Telefone, CPF, CNPJ, código de barras, valor. */
  media: 'max-w-[320px]',
  /** Nome, e-mail, razão social, endereço. */
  longa: 'max-w-[520px]',
  total: '',
}

type PropsCampo = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> & {
  rotulo: string
  erro?: string
  dica?: string
  /** Conteúdo à direita do rótulo, como "Esqueci minha senha". */
  acessorio?: ReactNode
  variante?: VarianteCampo
  /** Sobrepõe a altura da variante. Use só quando o handoff pedir. */
  altura?: number
  /** Teto de largura, pelo conteúdo esperado. Ver `LarguraCampo`. */
  largura?: LarguraCampo
}

export function Campo({
  rotulo,
  erro,
  dica,
  acessorio,
  variante = 'sistema',
  altura,
  largura = 'total',
  ...resto
}: PropsCampo) {
  const id = useId()
  const idErro = `${id}-erro`
  const idDica = `${id}-dica`
  const medida = MEDIDAS[variante]

  return (
    <div className={LARGURA[largura]}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-[13px] font-semibold text-texto">
          {rotulo}
        </label>
        {acessorio}
      </div>

      {/*
        `campo` carrega os cinco estados da spec 01 — padrão, hover, foco,
        erro e desabilitado. É `aria-invalid` que liga o estado de erro no
        CSS, então o atributo de acessibilidade e o visual não conseguem
        divergir: quem esquecer o `aria-invalid` perde a borda vermelha e
        percebe na hora.

        A altura vai em `style` porque inline vence classe: o sistema usa
        38px e as telas de acesso usam 52px, sem precisar de segunda classe.
      */}
      <input
        {...resto}
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? idErro : dica ? idDica : undefined}
        style={{ height: altura ?? medida.altura }}
        className={`campo ${medida.raio}`}
      />

      {erro && <MensagemDeErro id={idErro}>{erro}</MensagemDeErro>}
      {!erro && dica && (
        <p id={idDica} className="mt-[6px] text-[12.5px] text-texto-fraco">
          {dica}
        </p>
      )}
    </div>
  )
}

type PropsSenha = Omit<PropsCampo, 'type'> & {
  /** Mostra o medidor de força abaixo do campo. */
  comMedidor?: boolean
}

/**
 * Campo de senha com o botão Mostrar/Ocultar do handoff.
 *
 * O botão alterna `type` entre password e text, exatamente como o protótipo —
 * é o que permite conferir a senha antes de submeter, e reduz muito o "senha
 * incorreta" de quem digita em teclado de tablet.
 *
 * A VARIANTE existe pelo mesmo motivo que no `Campo`, e a falta dela era um
 * bug: este componente fixava 52 px e `rounded-auth`, que são as medidas das
 * telas de ACESSO. Dentro do sistema — a troca de senha do perfil — ele saía
 * catorze pixels mais alto que todos os outros campos ao lado, com um raio
 * diferente. O padrão é `sistema`, que é onde ele mais aparece.
 */
export function CampoSenha({
  rotulo,
  erro,
  acessorio,
  comMedidor,
  variante = 'sistema',
  largura = 'total',
  ...resto
}: PropsSenha) {
  const id = useId()
  const idErro = `${id}-erro`
  const [visivel, setVisivel] = useState(false)
  const medida = MEDIDAS[variante]

  const valor = typeof resto.value === 'string' ? resto.value : ''

  // O botão acompanha a altura do campo, com 4 px de folga de cada lado — em
  // 38 px ele fica com 30, em 52 fica com 44. Um valor fixo daria um botão
  // colado na borda no campo baixo e boiando no alto.
  const alturaDoBotao = medida.altura - 8

  return (
    <div className={LARGURA[largura]}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-[13px] font-semibold text-texto">
          {rotulo}
        </label>
        {acessorio}
      </div>

      <div className="relative">
        <input
          {...resto}
          id={id}
          type={visivel ? 'text' : 'password'}
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro ? idErro : undefined}
          style={{ height: medida.altura }}
          className={`campo ${medida.raio} pr-[86px]`}
        />
        <button
          type="button"
          onClick={() => setVisivel((atual) => !atual)}
          aria-pressed={visivel}
          style={{ height: alturaDoBotao, top: 4 }}
          className="absolute right-1 flex items-center rounded-sistema bg-divisoria px-3 text-[12px] font-semibold text-texto hover:bg-borda"
        >
          {visivel ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>

      {comMedidor && <MedidorDeSenha senha={valor} />}

      {erro && <MensagemDeErro id={idErro}>{erro}</MensagemDeErro>}
    </div>
  )
}

/**
 * Mensagem de erro de campo.
 *
 * COM ícone, como a spec 01 exige. O ícone não é enfeite: é o segundo canal
 * além da cor, e sem ele quem não distingue vermelho de cinza não sabe que
 * aquela linha é um erro.
 */
export function MensagemDeErro({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p
      id={id}
      role="alert"
      className="mt-[6px] flex items-center gap-1.5 text-[12.5px] font-medium text-erro"
    >
      <Icone nome="error" tamanho={14} />
      {children}
    </p>
  )
}

type Nivel = { largura: string; cor: string; rotulo: string }

/**
 * Medidor de força de senha do handoff: 0 caracteres nada, < 8 fraca, < 12
 * média, ≥ 12 forte.
 *
 * É orientação de UX e não substitui a validação do backend — que exige 8
 * caracteres independentemente do que este medidor mostrar.
 */
export function MedidorDeSenha({ senha }: { senha: string }) {
  const nivel = calcularNivel(senha.length)

  return (
    <div className="my-[10px] mb-[18px] flex items-center gap-[10px]">
      <div
        className="h-1 flex-1 overflow-hidden rounded-full bg-divisoria"
        role="progressbar"
        aria-label="Força da senha"
        aria-valuetext={nivel.rotulo || 'sem senha'}
      >
        <div
          className="h-full rounded-full transition-[width]"
          style={{ width: nivel.largura, background: nivel.cor }}
        />
      </div>
      <span
        className="min-w-[44px] text-right text-xs font-semibold"
        style={{ color: nivel.cor }}
      >
        {nivel.rotulo}
      </span>
    </div>
  )
}

function calcularNivel(tamanho: number): Nivel {
  if (tamanho === 0) return { largura: '0%', cor: '#E5E7EB', rotulo: '' }
  if (tamanho < 8) return { largura: '33%', cor: '#DC2626', rotulo: 'Fraca' }
  if (tamanho < 12) return { largura: '66%', cor: '#F59E0B', rotulo: 'Média' }
  return { largura: '100%', cor: '#16A34A', rotulo: 'Forte' }
}

/**
 * Chave liga/desliga — 42×24px, trilho na marca quando ligada, transição de
 * 160ms (catálogo de componentes, seção "Seleção").
 *
 * Existe separada da `Caixa` porque as duas dizem coisas diferentes: caixa é
 * "inclua isto na lista", chave é "ligue este comportamento". A chave carrega
 * `ajuda` porque comportamento sem consequência escrita vira aposta — e o que
 * ela liga aqui mexe em saldo de estoque.
 */
export function Chave({
  ligada,
  aoMudar,
  rotulo,
  ajuda,
  desabilitada = false,
}: {
  ligada: boolean
  aoMudar: (valor: boolean) => void
  rotulo: string
  ajuda?: ReactNode
  desabilitada?: boolean
}) {
  const id = useId()

  return (
    <div className={`flex items-start gap-3.5 ${desabilitada ? 'opacity-55' : ''}`}>
      <div className="min-w-0 flex-1">
        <label
          id={id}
          onClick={() => !desabilitada && aoMudar(!ligada)}
          className={`block text-[13.5px] font-semibold text-tinta ${desabilitada ? '' : 'cursor-pointer'}`}
        >
          {rotulo}
        </label>
        {ajuda && <p className="mt-1 max-w-[80ch] text-[12.5px] leading-[1.5] text-texto-suave">{ajuda}</p>}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={ligada}
        aria-labelledby={id}
        disabled={desabilitada}
        onClick={() => aoMudar(!ligada)}
        className={`mt-0.5 flex h-6 w-[42px] shrink-0 items-center rounded-full p-[3px] transition-colors duration-150 ${
          desabilitada ? 'bg-borda' : ligada ? 'bg-marca' : 'bg-inativo'
        }`}
      >
        <span
          className="h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform duration-150"
          style={{ transform: ligada ? 'translateX(18px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  )
}

/** Caixa de seleção do handoff: 20px, raio 5px, marca quando ativa. */
export function Caixa({
  marcada,
  aoMudar,
  children,
  tamanho = 20,
}: {
  marcada: boolean
  aoMudar: (valor: boolean) => void
  children: ReactNode
  tamanho?: number
}) {
  const id = useId()

  return (
    <div className="flex items-start gap-[10px]">
      <button
        type="button"
        role="checkbox"
        aria-checked={marcada}
        aria-labelledby={id}
        onClick={() => aoMudar(!marcada)}
        style={{ width: tamanho, height: tamanho }}
        className={`flex shrink-0 items-center justify-center rounded-[5px] border-[1.5px] text-xs font-bold text-white ${
          marcada ? 'border-marca bg-marca' : 'border-inativo bg-white'
        }`}
      >
        {marcada ? '✓' : ''}
      </button>
      <label
        id={id}
        onClick={() => aoMudar(!marcada)}
        className="text-sm text-texto"
      >
        {children}
      </label>
    </div>
  )
}
